import { clamp, formatMoneyInput, groupThousands, parseMoney, splitMoney } from './form-values.js';

/**
 * Live formatting for form inputs: money fields get thousands separators
 * while typing, percent fields show «18,0 %» when not being edited.
 * Pure helpers first, DOM attachers below.
 */

const NBSP = '\u00a0';
const PERCENT_SUFFIX = `${NBSP}%`;

/**
 * While typing: groups thousands with spaces and shows kopecks after a comma (`.` or `,` typed):
 * `1234567` → `1 234 567`, `7000000.5` → `7 000 000,5`. Leading zeros stay, so deleting the
 * first digit of `8 000 000` leaves `000 000` for the next digit; the blur handler tidies up.
 */
export function formatMoneyLive(raw: string): string {
  const { whole, fraction, point } = splitMoney(raw);
  if (!point) return groupThousands(whole);
  return `${groupThousands(whole || '0')},${fraction}`;
}

const SIGNIFICANT = /[\d.,]/;

/** Number of digits and decimal separators in `text`: what the caret position is measured in. */
export function significantCount(text: string): number {
  let count = 0;
  for (const char of text) if (SIGNIFICANT.test(char)) count += 1;
  return count;
}

/** Index just after the `count`-th digit or separator of `text` (0 for none; end of text when there are fewer). */
export function caretAfterDigits(text: string, count: number): number {
  if (count <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < text.length; i += 1) {
    if (SIGNIFICANT.test(text[i])) {
      seen += 1;
      if (seen === count) return i + 1;
    }
  }
  return text.length;
}

/** `18` → `18,0 %`, `16.9` → `16,9 %` (one decimal, non-breaking space before the sign). */
export function formatPercentInput(value: number): string {
  if (!Number.isFinite(value)) return '';
  const text = value.toLocaleString('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1, useGrouping: false });
  return `${text}${PERCENT_SUFFIX}`;
}

/** Accepts `18`, `18,5`, `18.5`, `18,5 %`; `NaN` when there is no number. */
export function parsePercent(raw: string): number {
  const cleaned = raw.replace(/[%\s]/g, '').replace(',', '.');
  if (!cleaned) return Number.NaN;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : Number.NaN;
}

/** `18,0 %` → `18,0`: the text a user edits after focusing a percent field. */
export function editablePercent(display: string): string {
  return display.replace(/\s*%\s*$/, '').trim();
}

const moneyAttached = new WeakSet<HTMLInputElement>();
const percentAttached = new WeakSet<HTMLInputElement>();

/**
 * Reformats the value with thousands separators on every `input` event and
 * keeps the caret after the same number of digits it was after before (or at the end).
 * Runs on the element, i.e. before the form's own `input` listeners.
 */
export function attachMoneyFormatting(input: HTMLInputElement): void {
  if (moneyAttached.has(input)) return;
  moneyAttached.add(input);
  input.addEventListener('input', () => {
    const before = input.value;
    const next = formatMoneyLive(before);
    if (next === before) return;
    const caret = input.selectionStart ?? before.length;
    const atEnd = caret >= before.length;
    const countBeforeCaret = significantCount(before.slice(0, caret));
    input.value = next;
    const position = atEnd ? next.length : caretAfterDigits(next, countBeforeCaret);
    input.setSelectionRange(position, position);
  });
  // Leaving the field drops leading zeros and an empty kopeck part: `000 000` → `0`, `7,` → `7`.
  input.addEventListener('blur', () => {
    if (!input.value.trim()) return;
    const tidy = formatMoneyInput(parseMoney(input.value));
    if (tidy !== input.value) input.value = tidy;
  });
}

/** Attaches live money formatting to every `input[data-money]` under `root`. */
export function attachMoneyFormattingAll(root: ParentNode): void {
  root.querySelectorAll<HTMLInputElement>('input[data-money]').forEach(attachMoneyFormatting);
}

export type PercentOptions = {
  /** Value used when the field does not contain a number on blur. */
  fallback: number;
  min?: number;
  max?: number;
};

/**
 * Percent field behaviour: on focus the « %» suffix is stripped so the user
 * edits `18,0`; on blur the value is parsed, clamped, rounded to one decimal
 * and shown as `18,0 %`. When that changes the number, an `input` event is
 * dispatched so the form recomputes with the corrected value.
 */
export function attachPercentFormatting(input: HTMLInputElement, options: PercentOptions): void {
  if (percentAttached.has(input)) return;
  percentAttached.add(input);
  const min = options.min ?? 0;
  const max = options.max ?? 100;

  input.addEventListener('focus', () => {
    const editable = editablePercent(input.value);
    if (editable === input.value) return;
    // Rewriting the value collapses the selection made by a Tab or by an automated fill;
    // keep the whole number selected so that typing replaces it.
    input.value = editable;
    input.select();
  });

  input.addEventListener('blur', () => {
    const parsed = parsePercent(input.value);
    const value = Number.isNaN(parsed) ? options.fallback : clamp(parsed, min, max);
    const rounded = Math.round(value * 10) / 10;
    input.value = formatPercentInput(rounded);
    if (rounded !== parsed) input.dispatchEvent(new Event('input', { bubbles: true }));
  });
}
